import { green, red, yellow } from '@mui/material/colors'
import { useEffect, useState } from 'react'

import { InputMaybe, Ordering } from '@/graphql'

export interface MockStore {
  id: string
  location: {
    name: string
  }
  totalRevenue: number
  emptyFacings: number
  compliance: number
  restockCompletion: number
  taskCompletion: number
}

export interface MockStoreStatsQueryResult {
  loading: boolean
  error: null | { message: string }
  data: {
    tenantStoreStats: {
      results: MockStore[]
      totalCount: number
    }
  }
}

export interface StoreStatsQueryVariables {
  filters: Record<string, unknown>
  order: Partial<Record<keyof MockStore, 'ASC' | 'DESC'>>
  pagination: {
    limit: number
    offset: number
  }
}

export type MockStoreStatsOrder = {
  name?: InputMaybe<Ordering>
  totalRevenue?: InputMaybe<Ordering>
  emptyFacings?: InputMaybe<Ordering>
  compliance?: InputMaybe<Ordering>
  restockCompletion?: InputMaybe<Ordering>
  taskCompletion?: InputMaybe<Ordering>
}

export interface Thresholds {
  compliance: number[]
  restockCompletion: number[]
  taskCompletion: number[]
  emptyFacings: number[]
}

export function getRAGColor(
  thresholds: number[],
  value?: number | null,
  reverse = false,
): string | undefined {
  if (value === undefined || value === null) {
    return undefined
  }
  if (reverse) {
    if (thresholds.length == 1 && value > thresholds[0]) return red[700]
    return ''
  }

  if (value < thresholds[0]) return red[700]
  if (value < thresholds[1]) return yellow[700]
  return green[700]
}

export const mockThresholds: Thresholds = {
  compliance: [0.5, 0.8],
  restockCompletion: [0.5, 0.8],
  taskCompletion: [0.5, 0.8],
  emptyFacings: [10],
}

export const mockStoreStatsData: MockStoreStatsQueryResult = {
  loading: false,
  error: null,
  data: {
    tenantStoreStats: {
      results: [
        {
          id: 'store-001',
          location: { name: 'This one' },
          totalRevenue: 15234.56,
          emptyFacings: 3,
          compliance: 0.8,
          restockCompletion: 0.6,
          taskCompletion: 0.75,
        },
        {
          id: 'store-002',
          location: { name: 'That one' },
          totalRevenue: 9823.75,
          emptyFacings: 5,
          compliance: 0.72,
          restockCompletion: 0.85,
          taskCompletion: 0.6,
        },
        {
          id: 'store-003',
          location: { name: 'The one over there' },
          totalRevenue: 20456.12,
          emptyFacings: 2,
          compliance: 0.9,
          restockCompletion: 0.95,
          taskCompletion: 0.88,
        },
        {
          id: 'store-004',
          location: { name: 'The little one' },
          totalRevenue: 10356.16,
          emptyFacings: 20,
          compliance: 0.2,
          restockCompletion: 0.05,
          taskCompletion: 0.1,
        },
        {
          id: 'store-005',
          location: { name: 'The busy one' },
          totalRevenue: 1000000,
          emptyFacings: 8,
          compliance: 0.6,
          restockCompletion: 0.5,
          taskCompletion: 0.7,
        },
        {
          id: '18fbea54-cfb8-4e64-bcd0-6288c489033e',
          location: { name: 'Another one' },
          totalRevenue: 100000,
          emptyFacings: 12,
          compliance: 0.73,
          restockCompletion: 0.32,
          taskCompletion: 0.23,
        },
      ],
      totalCount: 6,
    },
  },
}

export function useMockStoreStatsQuery(variables: StoreStatsQueryVariables): {
  loading: boolean
  error: null | { message: string }
  data: {
    tenantStoreStats: {
      results: MockStore[]
      totalCount: number
    }
  }
} {
  const allResults = mockStoreStatsData.data.tenantStoreStats.results
  const totalCount = allResults.length

  const [orderBy, order] = Object.entries(variables.order)[0] ?? ['id', 'ASC']
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoading(false)
    }, 2000)

    return () => clearTimeout(timeout)
  }, [])

  const sorted = [...allResults].sort((a, b) => {
    let aVal: string | number = ''
    let bVal: string | number = ''

    if (orderBy === 'name') {
      aVal = a.location.name
      bVal = b.location.name
    } else {
      aVal = a[orderBy as keyof MockStore] as any
      bVal = b[orderBy as keyof MockStore] as any
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return order === 'ASC' ? aVal - bVal : bVal - aVal
    }

    return String(aVal).localeCompare(String(bVal)) * (order === 'ASC' ? 1 : -1)
  })

  const paginated = sorted.slice(
    variables.pagination.offset,
    variables.pagination.offset + variables.pagination.limit,
  )

  return {
    loading,
    error: null,
    data: {
      tenantStoreStats: {
        results: paginated,
        totalCount,
      },
    },
  }
}
